import { Search } from "@clockwise/design-system/icons";
import { Popper, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import { differenceBy } from "lodash";
import React from "react";
import { PersonChip } from "../person-chip";
import { styles } from "./person-selector.styles";
import { PersonSuggestion } from "./person-suggestion";
import { Person } from "./types";

const useStyles = makeStyles(styles);

export type Props<T> = {
  onAdd: (person: T) => void;
  onDelete?: (person: T) => void;
  onSearch: (query: string) => void;
  suggestions: T[];
  selections: T[];
  inviteOption?: React.ReactNode;
  typing?: boolean;
  loading?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  hideInput?: boolean;
  popperPosition?: "bottom-start" | "top-start";
  searchString?: string;
  popperAnchorEl?: HTMLElement | null;
};

export const PersonSelector = <T extends Person>({
  onAdd,
  onDelete,
  onSearch,
  suggestions,
  inviteOption,
  selections,
  typing,
  loading,
  placeholder = "Search",
  fullWidth = false,
  hideInput = false,
  popperPosition = "bottom-start",
  popperAnchorEl,
}: Props<T>) => {
  const classes = useStyles();

  return (
    <Autocomplete
      multiple
      disableClearable
      filterSelectedOptions
      autoHighlight
      loading={loading}
      options={suggestions}
      getOptionLabel={(option) => {
        return option.email.toString();
      }}
      open={hideInput ? true : undefined}
      filterOptions={(options) => options} // disable filtering, see https://mui.com/material-ui/react-autocomplete/
      freeSolo={typing /* Hides the noOptionsText while user is typing */}
      noOptionsText={inviteOption || "Not found"}
      value={selections}
      onChange={(_e, value) => {
        const sanitizedValues = value.filter((v): v is T => !!v && typeof v !== "string");
        const added = differenceBy(sanitizedValues, selections, "email");
        const removed = differenceBy(selections, sanitizedValues, "email");
        added.forEach((p) => onAdd(p));
        removed.forEach((p) => onDelete?.(p));
      }}
      PopperComponent={({ children, ...popperProps }) => {
        return (
          <Popper
            {...popperProps}
            keepMounted
            anchorEl={popperAnchorEl ?? popperProps.anchorEl}
            placement={popperPosition}
            open={hideInput ? true : popperProps.open}
          >
            {children}
          </Popper>
        );
      }}
      onInputChange={(_e, value) => onSearch(value)}
      renderTags={(value: T[], getTagProps) =>
        value.map((person, index) => {
          // getTagProps returns an object with the following props:
          // key, data-tag-index, tabIndex, onDelete, className, disabled
          const tagProps = getTagProps({ index });
          return (
            <PersonChip
              key={person.email}
              email={person.email}
              name={person.name}
              imageUrl={person.externalImageUrl}
              {...tagProps}
            />
          );
        })
      }
      renderInput={({ InputProps, inputProps, ...otherProps }) => {
        return (
          <TextField
            {...otherProps}
            classes={{ root: classes.searchField }}
            placeholder={selections.length === 0 ? placeholder : ""}
            fullWidth={fullWidth}
            autoFocus={!hideInput}
            inputProps={{
              ...inputProps,
            }}
            InputProps={{
              ...InputProps,
              className: classNames(
                InputProps.className,
                classes.searchInput,
                hideInput && "cw-invisible",
              ),
              disableUnderline: true,
              startAdornment: [
                <Search key="input-search-icon" className={classes.searchIcon} />,
                ...React.Children.toArray(InputProps.startAdornment),
              ],
              endAdornment: null,
            }}
          />
        );
      }}
      renderOption={(person: T) => <PersonSuggestion person={person} />}
      popupIcon={null}
    />
  );
};
